<template>
  <div class="fixed inset-0 -z-50 bg-gray-900">
    <!-- Moonlight background layer -->
    <div class="absolute inset-0 bg-moonlight opacity-80"></div>
    <div class="absolute inset-0 bg-moonlight-subtle animate-pulse-slow opacity-90"></div>

    <!-- Top right glow -->
    <div class="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-indigo-100/10 blur-3xl"></div>
    <div class="absolute -top-10 -right-10 w-60 h-60 rounded-full bg-white/5 blur-2xl"></div>

    <!-- ISS -->
    <div class="absolute inset-0 overflow-hidden">
      <div class="absolute top-0 left-0 w-full h-full">
        <div class="animate-iss-transit w-full h-full">
          <div class="absolute h-1 w-1 rounded-full bg-white/90"></div>
        </div>
      </div>
    </div>

    <!-- Stars -->
    <div class="absolute inset-0">
      <!-- Bright stars -->
      <div v-for="star in brightStars" :key="star.id" class="absolute h-0.5 w-0.5 rounded-full bg-white/80"
        :class="star.animation" :style="{ top: star.top + '%', left: star.left + '%' }">
      </div>

      <!-- Dimmer stars -->
      <div v-for="star in dimStars" :key="star.id" class="absolute h-0.5 w-0.5 rounded-full bg-white/60"
        :class="star.animation" :style="{ top: star.top + '%', left: star.left + '%' }">
      </div>

      <!-- Faintest stars -->
      <div v-for="star in faintStars" :key="star.id" class="absolute h-px w-px rounded-full bg-white/40"
        :class="star.animation" :style="{ top: star.top + '%', left: star.left + '%' }">
      </div>

      <!-- Tiny distant stars -->
      <div v-for="star in tinyStars" :key="star.id" class="absolute h-px w-px rounded-full bg-white/30"
        :class="star.animation" :style="{ top: star.top + '%', left: star.left + '%' }">
      </div>
    </div>
  </div>



  <!-- Web Dev Content -->
  <div v-if="currentService === 'Web Dev'" id="app" class="relative text-cream min-h-screen py-8">
    <div class="max-w-6xl mx-auto px-4">
      <!-- Hero Section for Web Dev -->
      <div class="relative mb-16 z-10 py-16 rounded-2xl overflow-hidden
           bg-gradient-to-b from-gray-800/60 to-gray-900/60 backdrop-blur-lg
           border border-indigo-500/30
           shadow-[0_0_30px_rgba(79,70,229,0.15)]
           max-w-7xl mx-auto">
        <div class="text-center space-y-12">
          <div class="space-y-4">
            <p class="text-xl font-light text-indigo-200/90">Hey, I'm John</p>
            <p class="text-6xl font-normal leading-normal 
                      bg-gradient-to-r from-white via-indigo-100 to-white 
                      bg-clip-text text-transparent
                      drop-shadow-[0_0_25px_rgba(255,255,255,0.1)]">
              Let's create together
            </p>
          </div>
        </div>
      </div>

      <!-- About Section -->
      <section class="relative -mt-8 mb-24">
        <div class="max-w-5xl mx-auto">
          <!-- Intro -->
          <div class="text-center mb-12">
            <p class="text-xl font-extralight text-cream/90">
              I'm a full-stack web developer with 20 years of experience.
            </p>
          </div>

          <!-- Service Cards -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
            <!-- Website Management Card -->
            <div class="group bg-gradient-to-b from-gray-800/50 via-indigo-900/20 to-gray-900/50 
                        backdrop-blur-sm rounded-xl p-8 
                        border border-indigo-700/20
                        hover:border-indigo-500/30 hover:from-gray-800/60 hover:via-indigo-900/30 hover:to-gray-900/60
                        transition-all duration-300
                        flex flex-col min-h-[24rem]">
              <div>
                <h3 class="text-2xl font-light mb-4 text-cream/90">Website Creation & Upkeep</h3>
                <p class="text-base font-extralight leading-relaxed mb-6 text-cream/90">
                  Perfect for individuals, small businesses, and non-profits looking for a professional web presence.
                  <br /><br />
                  I specialize in platforms like WordPress, Joomla, and Squarespace, ensuring your website stays modern
                  and functional.
                </p>
              </div>
              <div class="mt-auto">
                <ContactActions
                  :form-open="activeContactForm === 'web-website'"
                  :contact-form="contactForm"
                  :status="contactStatus"
                  @toggle="toggleContactForm('web-website')"
                  @submit="submitContactForm"
                  @update:contact-form="contactForm = $event"
                />
              </div>
            </div>

            <!-- Full-stack Card -->
            <div class="group bg-gradient-to-b from-gray-800/50 via-indigo-900/20 to-gray-900/50 
                        backdrop-blur-sm rounded-xl p-8 
                        border border-indigo-700/20
                        hover:border-indigo-500/30 hover:from-gray-800/60 hover:via-indigo-900/30 hover:to-gray-900/60
                        transition-all duration-300
                        flex flex-col min-h-[24rem]">
              <div>
                <h3 class="text-2xl font-light mb-4 text-cream/90">Freelance Full-Stack Engineer</h3>
                <p class="text-base font-extralight leading-relaxed mb-6 text-cream/90">
                  Bringing enterprise-level expertise to your project.
                  <br /><br />
                  Primarily work in Vue, Pinia, PHP, and Laravel. Experienced in building SPAs, API integrations
                  (including AI), and creating complex web applications.
                  <br /><br />
                  Available for both freelance and fractional work.
                </p>
              </div>
              <div class="mt-auto">
                <ContactActions
                  :form-open="activeContactForm === 'web-fullstack'"
                  :contact-form="contactForm"
                  :status="contactStatus"
                  @toggle="toggleContactForm('web-fullstack')"
                  @submit="submitContactForm"
                  @update:contact-form="contactForm = $event"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Portfolio Section with Grid -->
      <section class="mb-24">
        <div class="max-w-5xl mx-auto">
          <h2 class="text-4xl font-normal mb-12">Portfolio</h2>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div v-for="(project, index) in projects" :key="index" @click="openProject(project)"
              class="portfolio-card group relative hover:shadow-lg transition-all cursor-pointer
                        bg-gradient-to-b from-gray-800/30 via-indigo-900/10 to-gray-900/30 
                        backdrop-blur-sm rounded-xl overflow-hidden
                        border-2 border-indigo-700/30
                        hover:border-indigo-500/40 hover:from-gray-800/40 hover:via-indigo-900/20 hover:to-gray-900/40">
              <div class="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/60 to-transparent 
                          opacity-80 group-hover:opacity-60 transition-opacity duration-300"></div>
              <img :src="project.images[0]" :alt="project.title" class="w-full h-64 object-cover">
              <div class="absolute bottom-0 left-0 p-6 w-full">
                <h3 class="text-2xl font-light text-cream/90 group-hover:text-cream 
                           transition-colors duration-300">{{ project.title }}</h3>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Project Modal -->
      <div v-if="selectedProject" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
        @click="closeProject">
        <div class="relative max-w-4xl w-full bg-gradient-to-b from-gray-700/70 via-indigo-800/40 to-gray-800/70 
                    backdrop-blur-sm rounded-xl p-10 max-h-[90vh] overflow-y-auto
                    border border-indigo-500/30
                    hover:border-indigo-400/40 transition-all duration-300" @click.stop>
          <!-- Close button -->
          <button @click="closeProject"
            class="absolute top-4 right-4 text-indigo-200 hover:text-indigo-100 transition-colors">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <!-- Image carousel -->
          <div class="relative mb-6">
            <img :src="selectedProject.images[modalImageIndex]" :alt="selectedProject.title" class="w-full rounded-lg"
              style="max-height: 80vh; object-fit: contain;">

            <!-- Navigation arrows -->
            <button v-if="selectedProject.images.length > 1" @click="prevImage" class="absolute left-2 top-1/2 -translate-y-1/2 bg-indigo-900/50 hover:bg-indigo-800/60 
                           p-2 rounded-full transition-all duration-300">
              <svg class="w-6 h-6 text-indigo-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button v-if="selectedProject.images.length > 1" @click="nextImage" class="absolute right-2 top-1/2 -translate-y-1/2 bg-indigo-900/50 hover:bg-indigo-800/60 
                           p-2 rounded-full transition-all duration-300">
              <svg class="w-6 h-6 text-indigo-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>

            <!-- Image indicators -->
            <div v-if="selectedProject.images.length > 1"
              class="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2">
              <button v-for="(_, imgIndex) in selectedProject.images" :key="imgIndex"
                @click="modalImageIndex = imgIndex" :class="[
                  'w-2 h-2 rounded-full transition-all',
                  modalImageIndex === imgIndex
                    ? 'bg-indigo-100'
                    : 'bg-indigo-300/50 hover:bg-indigo-200/70'
                ]">
              </button>
            </div>
          </div>

          <!-- Project details -->
          <h3 class="text-3xl font-light mb-4 text-cream">{{ selectedProject.title }}</h3>
          <p class="text-cream/90 font-extralight mb-6">{{ selectedProject.description }}</p>

          <!-- Technologies used -->
          <div class="flex flex-wrap gap-2">
            <span v-for="(tech, index) in selectedProject.technologies" :key="index" class="px-3 py-1 bg-indigo-800/40 border border-indigo-500/40 
                         rounded-full text-xs font-extralight text-indigo-100">
              {{ tech }}
            </span>
          </div>
        </div>
      </div>

      <!-- Experience Section -->
      <section class="mb-24">
        <div class="max-w-5xl mx-auto">
          <h2 class="text-4xl font-normal mb-12">Experiences</h2>
          <div class="space-y-8">
            <div v-for="(experience, index) in experiences" :key="index" class="group bg-gradient-to-b from-gray-800/50 via-indigo-900/20 to-gray-900/50 
                        backdrop-blur-sm rounded-xl p-8 
                        border border-indigo-700/20
                        hover:border-indigo-500/30 hover:from-gray-800/60 hover:via-indigo-900/30 hover:to-gray-900/60
                        transition-all duration-300
                        flex flex-col min-h-[16rem]">
              <div>
                <div class="flex justify-between items-start mb-4">
                  <div>
                    <h3 class="text-2xl font-light mb-2 text-cream">{{ experience.role }}</h3>
                    <p class="text-secondary text-sm font-extralight text-cream/80">{{ experience.company }}</p>
                  </div>
                  <span class="text-indigo-100">{{ experience.period }}</span>
                </div>
                <p class="text-base font-extralight leading-relaxed mb-6 text-cream/90">{{ experience.description }}</p>
              </div>
              <div class="mt-auto flex flex-wrap gap-2">
                <span v-for="(tech, tIndex) in experience.technologies" :key="tIndex" class="px-3 py-1 bg-indigo-800/40 border border-indigo-500/40 
                             rounded-full text-xs font-extralight text-indigo-100">
                  {{ tech }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Back to AI Link -->
      <div class="text-center pb-12">
        <p class="text-base font-extralight text-cream/70">
          Looking for AI engineering? —
          <button @click="updatePath('AI')"
            class="text-indigo-400 hover:text-indigo-300 underline underline-offset-2 transition-colors">
            view AI services
          </button>
        </p>
      </div>
    </div>
  </div>

  <!-- AI Content -->
  <AiV2Page
    v-if="currentService === 'AI'"
    :form-open="activeContactForm === 'ai'"
    :contact-form="contactForm"
    :status="contactStatus"
    @toggle="toggleContactForm('ai')"
    @submit="submitContactForm"
    @update:contact-form="contactForm = $event"
    @go-web="updatePath('Web Dev')"
  />

</template>

<script>
import ContactActions from './components/ContactActions.vue';
import AiV2Page from './components/AiV2Page.vue';

function generateStars(count, preferredAreas = []) {
  return Array.from({ length: count }, (_, i) => {
    let top, left;

    if (preferredAreas.length > 0 && Math.random() < 0.7) {
      // 70% chance to place stars in preferred areas
      const area = preferredAreas[Math.floor(Math.random() * preferredAreas.length)];
      top = area.topMin + Math.random() * (area.topMax - area.topMin);
      left = area.leftMin + Math.random() * (area.leftMax - area.leftMin);
    } else {
      top = Math.random() * 100;
      left = Math.random() * 100;
    }

    return {
      id: i,
      top,
      left,
      animation: `animate-twinkle-${Math.floor(Math.random() * 3) + 1}`
    };
  });
}

export default {
  name: 'App',
  components: {
    ContactActions,
    AiV2Page
  },
  data() {
    const preferredAreas = [
      { topMin: 50, topMax: 100, leftMin: 0, leftMax: 40 },  // Bottom left
      { topMin: 0, topMax: 100, leftMin: 0, leftMax: 30 },   // Left side
      { topMin: 70, topMax: 100, leftMin: 0, leftMax: 100 }, // Bottom area
    ];

    return {
      brightStars: generateStars(10, preferredAreas),
      dimStars: generateStars(12, preferredAreas),
      faintStars: generateStars(18, preferredAreas),
      tinyStars: generateStars(10, preferredAreas),
      name: 'John',
      title: 'Web & AI Developer',
      shortDescription: 'Front-end to back-end, and beyond.',
      currentService: 'AI',
      experiences: [
        {
          role: 'Lead Developer',
          company: 'Jotverse',
          period: '2022 - Present',
          description: 'Leading the development of an AI-powered tutoring platform. Sole developer working across the stack.',
          technologies: ['Vue', 'Laravel', 'PHP', 'Redis', 'Pinecone', 'AWS', 'Pinia', 'Tailwind']
        },
        {
          role: 'Freelance Developer',
          company: 'DevSpry',
          period: '2022 - Present',
          description: 'Built custom websites for clients, including a large community organization, a business consultancy, and a nonprofit. Actively building own projects.',
          technologies: ['WordPress', 'Joomla', 'PHP', 'JavaScript', 'HTML', 'CSS', 'Bootstrap']
        },
        {
          role: 'Founder, Developer',
          company: 'E-Fusion Web Design',
          period: '2004 - 2017',
          description: 'Built custom CMS with vanilla php, css, js, and sold to several clients. Built various sites for hobbyists, businesses, societies, and sport teams.',
          technologies: ['JavaScript', 'HTML', 'CSS', 'PHP']
        }
      ],
      projects: [
        {
          title: 'Community Organization',
          images: ['/images/community_org.png', '/images/community_org_2.png', '/images/community_org_3.png'],
          description: 'Worked closely with the client to build their website from scratch, with a limited budget and tight deadline. Integrated newsletter and event management systems.',
          technologies: ['WordPress']
        },
        {
          title: 'Nonprofit',
          images: ['/images/nonprofit.png', '/images/nonprofit_2.png', '/images/nonprofit_3.png'],
          description: 'Worked with client to rebrand their website and build out new features, including ecommerce store, a new event management system, and pledge page. Long-term contract with low monthly fee.',
          technologies: ['Joomla']
        },
        {
          title: 'Business Consultancy',
          images: ['/images/business_consultancy.png', '/images/business_consultancy_2.png'],
          description: 'Worked with the client to build a website to showcase and market their services, including heavily customising a wordpress theme to match their design requirements.',
          technologies: ['WordPress']
        },
        {
          title: 'AI Startup',
          images: ['/images/ai_startup.png', '/images/ai_startup_2.png', '/images/ai_startup_3.png', '/images/ai_startup_4.png'],
          description: 'Built an AI tutor platfrom from scratch as the sole developer, including AI chatbot, workflows, and agents.',
          technologies: ['Vue.js', 'PHP', 'Laravel', 'Redis', 'Pinecone', 'Various AI models']
        }
      ],
      selectedProject: null,
      modalImageIndex: 0,
      activeContactForm: null,
      contactForm: {
        name: '',
        email: '',
        message: ''
      },
      contactStatus: null
    };
  },
  methods: {
    updateServiceFromPath() {
      const path = window.location.pathname;
      if (path === '/web') {
        this.currentService = 'Web Dev';
        return;
      }
      this.currentService = 'AI';
      if (path !== '/') {
        window.history.replaceState({}, '', '/');
      }
    },
    updatePath(service) {
      const path = service === 'Web Dev' ? '/web' : '/';
      window.history.pushState({}, '', path);
      this.currentService = service === 'Web Dev' ? 'Web Dev' : 'AI';
    },
    openProject(project) {
      this.selectedProject = project;
      this.modalImageIndex = 0;  // Reset modal image index when opening
    },
    closeProject() {
      this.selectedProject = null;
      this.modalImageIndex = 0;  // Reset modal image index when closing
    },
    nextImage() {
      if (!this.selectedProject) return;
      this.modalImageIndex =
        (this.modalImageIndex + 1) % this.selectedProject.images.length;
    },
    prevImage() {
      if (!this.selectedProject) return;
      this.modalImageIndex =
        this.modalImageIndex === 0
          ? this.selectedProject.images.length - 1
          : this.modalImageIndex - 1;
    },
    toggleContactForm(formId) {
      this.contactStatus = null;
      this.activeContactForm = this.activeContactForm === formId ? null : formId;
    },
    async submitContactForm() {
      this.contactStatus = 'sending';

      try {
        const response = await fetch('/api/contact', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(this.contactForm)
        });

        if (!response.ok) {
          throw new Error('Failed to send message');
        }

        this.contactStatus = 'success';
        this.contactForm = { name: '', email: '', message: '' };
      } catch {
        this.contactStatus = 'error';
      }
    }
  },
  mounted() {
    // Set initial service based on URL
    this.updateServiceFromPath();

    // Listen for back/forward navigation
    window.addEventListener('popstate', () => {
      this.updateServiceFromPath();
    });
  },
  beforeUnmount() {
    // Clean up event listener
    window.removeEventListener('popstate', this.updateServiceFromPath);
  }
};
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500&display=swap');

/* Base styles */
#app {
  font-family: 'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-weight: 300;
}

/* Heading styles */
h1,
h2,
h3 {
  font-weight: 400;
  background: linear-gradient(to right, #e2e8f0, #a5b4fc);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.text-6xl,
h2,
.portfolio-card h3 {
  text-shadow: 0 0 20px rgba(165, 180, 252, 0.1);
}

.portfolio-card {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.portfolio-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(165, 180, 252, 0.1);
}

.overview-tech {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.feature-bullet {
  display: block;
  position: relative;
  padding-left: 1.2rem;
  margin-bottom: 0.5rem;
  color: rgba(226, 232, 240, 0.9);
}

.feature-bullet:last-child {
  margin-bottom: 0;
}

.feature-bullet::before {
  content: "•";
  position: absolute;
  left: 0;
  color: rgba(165, 180, 252, 0.9);
}
</style>
